import { BaseApi } from './base.api'

const ADMIN_PREFIX = 'api/admin/i18n'

// ---- Languages --------------------------------------------------------------

export class I18nLanguageAdminApi extends BaseApi {
	constructor() {
		super(ADMIN_PREFIX)
	}

	/** GET /api/admin/i18n/languages[?activeOnly=true] */
	list({ activeOnly = false } = {}) {
		const qs = activeOnly ? '?activeOnly=true' : ''
		return this.get(`languages${qs}`)
	}

	/** GET /api/admin/i18n/languages/:code */
	getOne(code) {
		return this.get(`languages/${encodeURIComponent(code)}`)
	}

	/** POST /api/admin/i18n/languages */
	create(data) {
		return this.post('languages', data)
	}

	/** PUT /api/admin/i18n/languages/:code */
	update(code, data) {
		return this.put(`languages/${encodeURIComponent(code)}`, data)
	}

	/** DELETE /api/admin/i18n/languages/:code */
	remove(code) {
		return this.delete(`languages/${encodeURIComponent(code)}`)
	}
}

// ---- UI Translations --------------------------------------------------------

export class I18nTranslationAdminApi extends BaseApi {
	constructor() {
		super(ADMIN_PREFIX)
	}

	/**
	 * GET /api/admin/i18n/languages/:code/ui-translations
	 * @param {string} code
	 * @param {{ platform?: 'web'|'mobile', prefix?: string, pageNum?: number, pageSize?: number }} opts
	 *
	 * platform filter is applied client-side on the full list so the editor
	 * can show the correct baseline; the backend stores keys with the "mob."
	 * prefix for mobile strings and without prefix for web strings.
	 */
	list(code, { platform, prefix, pageNum = 1, pageSize = 9999 } = {}) {
		const params = new URLSearchParams()
		// Request all keys; platform filtering is done on the baseline side.
		if (prefix) params.set('prefix', prefix)
		params.set('pageNum', pageNum)
		params.set('pageSize', pageSize)
		return this.get(`languages/${encodeURIComponent(code)}/ui-translations?${params}`)
			.then((rs) => {
				// Filter by platform on returned DB overrides so the editor only
				// shows keys relevant to the current tab.
				if (!platform) return rs
				const isMob = (key) => key.startsWith('mob.')
				const data = Array.isArray(rs?.data)
					? rs.data.filter((r) =>
							platform === 'mobile' ? isMob(r.key) : !isMob(r.key)
					  )
					: []
				return { ...rs, data, total: data.length }
			})
	}

	/**
	 * PUT /api/admin/i18n/languages/:code/ui-translations
	 * Body: { entries: [{ key, value }] }
	 * Keys for mobile strings must already include the "mob." prefix.
	 */
	upsertBulk(code, entries) {
		return this.put(`languages/${encodeURIComponent(code)}/ui-translations`, { entries })
	}

	/**
	 * DELETE /api/admin/i18n/languages/:code/ui-translations/:key
	 * key is URL-encoded because it may contain dots.
	 */
	deleteOne(code, key) {
		return this.delete(
			`languages/${encodeURIComponent(code)}/ui-translations/${encodeURIComponent(key)}`
		)
	}

	/**
	 * DELETE /api/admin/i18n/languages/:code/ui-translations?platform=web|mobile
	 * Resets overrides for the given platform only, or all if no platform given.
	 */
	reset(code, platform) {
		const qs = platform ? `?platform=${encodeURIComponent(platform)}` : ''
		return this.delete(`languages/${encodeURIComponent(code)}/ui-translations${qs}`)
	}
}

// ---- Course Translations ----------------------------------------------------

export class I18nCourseAdminApi extends BaseApi {
	constructor() {
		super(ADMIN_PREFIX)
	}

	/**
	 * GET /api/admin/i18n/courses
	 * @param {{ categoryID?: number, q?: string, pageNum?: number, pageSize?: number }} opts
	 */
	listCourses({ categoryID, q, pageNum = 1, pageSize = 50 } = {}) {
		const params = new URLSearchParams()
		if (categoryID) params.set('categoryID', categoryID)
		if (q) params.set('q', q)
		params.set('pageNum', pageNum)
		params.set('pageSize', pageSize)
		return this.get(`courses?${params}`)
	}

	/** PUT /api/admin/i18n/courses/:id/translations/:lang */
	upsertCourseTranslation(folderID, lang, name) {
		return this.put(
			`courses/${folderID}/translations/${encodeURIComponent(lang)}`,
			{ name }
		)
	}

	/** GET /api/admin/i18n/categories */
	listCategories() {
		return this.get('categories')
	}

	/** PUT /api/admin/i18n/categories/:id/translations/:lang */
	upsertCategoryTranslation(categoryID, lang, name) {
		return this.put(
			`categories/${categoryID}/translations/${encodeURIComponent(lang)}`,
			{ name }
		)
	}
}

